import { isDefined } from '@sgme/fp';

import { parseStrike } from '@/App/tiles/Option/utils/strikeUtils.ts';
import type {
  BlotterLegOptionProductType,
  BlotterSingleOrCompoundOptionProductType,
} from '@/store/blotter/api/types/BlotterTradeEntry.ts';
import { extractDay, getCurrencyChoice, getTileSide, parseDecimal } from '@/store/blotter/blotterUtils.ts';
import type { BlotterOptionEntry } from '@/store/blotter/types/BlotterOptionEntry.ts';
import type {
  HedgeType,
  OptionLegProperties,
  OptionProductName,
  OptionProperties,
  OptionType,
  SettlementType,
} from '@/store/option/state.ts';

export function getOptionFromBlotterEntry(
  tileId: string,
  entry: BlotterOptionEntry,
  children: BlotterOptionEntry[],
): Partial<OptionProperties> {
  const legEntries = children.filter(({ product }) => product === 'Vanilla');
  let legs = legEntries
    .map((legEntry, index): OptionLegProperties | undefined => {
      const legOptionType = getOptionType(legEntry);

      if (legOptionType === null) {
        // in fx we error
        return undefined;
      }

      return {
        legId: `${tileId}/${index}`,
        legProductName: 'Vanilla',
        type: legOptionType,
        side: getTileSide(legEntry),
        expiryDate: extractDay(legEntry.expiry),
        strike: legEntry.strike === null ? undefined : parseStrike(legEntry.strike.toString()),
        notional: parseDecimal(legEntry.dealtAmount?.toString()),
        notionalCurrencyIndex: getCurrencyChoice(legEntry.dealtCurrency, legEntry.currencyPair),
        deliveryDate: extractDay(legEntry.deliveryDate),
        premiumPaymentDate: extractDay(legEntry.premiumPaymentDate),
      };
    })
    .filter(isDefined);

  // FIX for devci bug? in devci somme blotter entries have no legs
  if (legs.length === 0) {
    legs = [
      {
        legId: `${tileId}/0`,
        legProductName: 'Vanilla',
        type: getOptionType(entry) ?? 'Call',
        side: getTileSide(entry),
        expiryDate: extractDay(entry.expiry),
        strike: entry.strike === null ? undefined : parseStrike(entry.strike.toString()),
        notional: parseDecimal(entry.dealtAmount?.toString()),
        notionalCurrencyIndex: getCurrencyChoice(entry.dealtCurrency, entry.currencyPair),
        deliveryDate: extractDay(entry.deliveryDate),
        premiumPaymentDate: extractDay(entry.premiumPaymentDate),
      },
    ];
  }

  const firstLeg = legEntries[0];
  const hedgeType = getOptionHedgeType(entry);
  const tile: Partial<OptionProperties> = {
    hedgeType,
    currencyPair: entry.currencyPair,
    premiumType: {
      premiumCategory: 'AMOUNT',
      premiumCurrency: getCurrencyChoice(entry.premiumCurrency, entry.currencyPair) ?? 1,
    },
    settlementType: getSettlementType(entry.deliveryType) ?? getSettlementType(firstLeg?.deliveryType),
    cutOffMarketPlace: entry.cutOffPlace ?? firstLeg?.cutOffPlace ?? undefined,
    premiumPaymentDate: extractDay(entry.premiumPaymentDate) ?? extractDay(firstLeg?.premiumPaymentDate),
    legs,
  };
  return tile;
}

function getOptionType(blotterEntry: BlotterOptionEntry): OptionType | undefined {
  if (blotterEntry.product === 'Strategy' || blotterEntry.product !== 'Vanilla') {
    return undefined;
  }
  switch (blotterEntry.optionType) {
    case 'CE':
    case 'CECSD1':
    case 'CECSD2':
    case 'CEFWD':
    case 'Call':
      return 'Call';
    case 'PE':
    case 'PECSD1':
    case 'PECSD2':
    case 'PEFWD':
    case 'Put':
      return 'Put';
    default:
      return undefined;
  }
}

function getSettlementType(deliveryType: string | null | undefined): SettlementType | undefined {
  const value = deliveryType?.trim()?.toLowerCase();
  switch (value) {
    case 'cash':
      return 'Cash';
    case 'physical_spot':
      return 'Physical';
    default:
      return undefined;
  }
}

function getOptionHedgeType(hedgeEntry: BlotterOptionEntry | undefined): HedgeType {
  switch (hedgeEntry?.product) {
    case undefined:
      return 'Live';
    case 'Spot':
      return 'Spot';
    case 'Outright':
      return 'Forward';
    default:
      return 'Live';
  }
}

export function getProductFromBlotterProduct(
  product: BlotterSingleOrCompoundOptionProductType | BlotterLegOptionProductType,
): OptionProductName {
  switch (product) {
    case 'Vanilla':
    case 'Strangle':
    case 'Straddle':
    case 'RiskReversal':
      return product;
    default:
      return 'Vanilla';
  }
}
